import { sortAsc } from '@strata/helper-functions';
import { InlineMessage } from '@strata/react-inline-message';
import ISO6391 from 'iso-639-1';

// Cookie key value
export const ENVIRONMENT_COOKIE_KEY = 'ENVIRONMENT';

// Session storage key value
export const LOGIN_REDIRECT_KEY = 'LOGIN_REDIRECT';

export const Permissions = {
    WRITE_INTAKE_CASE: 'write:intake_case',
    WRITE_INTAKE_CASE_ADMIN: 'write:intake_case_admin',
} as const;

export const CustomTimeFrameOptions = { ALL_TIME: 'ALL_TIME' } as const;

export const IntakeSource = {
    PHONE: 'PHONE',
    FAX: 'FAX',
    ESCRIPT: 'ESCRIPT',
    TREATMENT_REQUIRES_PA: 'TR_REQ_PA',
} as const;

export const IntakeSourceDisplayNameMap = {
    [IntakeSource.PHONE]: 'Phone',
    [IntakeSource.FAX]: 'Fax',
    [IntakeSource.ESCRIPT]: 'eScript',
    [IntakeSource.TREATMENT_REQUIRES_PA]: 'Treatment',
} as const;

export const IntakeSourceOptions = Object.entries(IntakeSourceDisplayNameMap).map(
    ([value, name]) => ({ name, value }),
);

export const IntakeCaseSnoozeStatus = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    EXPIRED: 'EXPIRED',
} as const;

export const Determination = {
    OPEN: 'Open',
    CLOSED: 'Closed',
    APPROVED: 'Approved',
    DENIED: 'Denied',
} as const;

export const TransactionType = {
    PA_INITIATION_REQUEST: 'PAInitationRequest',
    PA_INITIATION_RESPONSE: 'PAInitiationResponse',
    PA_REQUEST: 'PARequest',
    PA_RESPONSE: 'PAResponse',
    PA_CANCEL_REQUEST: 'PACancelRequest',
    PA_CANCEL_RESPONSE: 'PACancelResponse',
    PA_APPEAL_REQUEST: 'PAAppealRequest',
    PA_APPEAL_RESPONSE: 'PAAppealResponse',
} as const;

export const IntakeStatusDetails = {
    PRESCR_NUMBER_MISSING: 'PRESCR_NUMBER_MISSING',
    FAILED_FAX: 'FAILED_FAX',
    PRESCR_OUTREACH_1: 'PRESCR_OUTREACH_1',
    PRESCR_OUTREACH_2: 'PRESCR_OUTREACH_2',
    PRESCR_OUTREACH_3: 'PRESCR_OUTREACH_3',
    PRESCR_OUTREACH_4: 'PRESCR_OUTREACH_4',
    PRESCR_OUTREACH_ADDTL: 'PRESCR_OUTREACH_ADDTL',
    PRESCRIBER_NOTIFICATION_OUTREACH: 'PRESCRIBER_NOTIFICATION_OUTREACH',
    PRESCRIBER_NOTIFICATION_OUTREACH_2: 'PRESCRIBER_NOTIFICATION_OUTREACH_2',
    PRESCRIBER_NOTIFICATION_OUTREACH_3: 'PRESCRIBER_NOTIFICATION_OUTREACH_3',
    FMH_TO_SUBMIT: 'FMH_TO_SUBMIT',
    PRESCR_TO_SUBMIT: 'PRESCR_TO_SUBMIT',
    PHARMACY_TO_SUBMIT: 'PHARMACY_TO_SUBMIT',
    SUBMISSION_OUTREACH_1: 'SUBMISSION_OUTREACH_1',
    SUBMISSION_OUTREACH_2: 'SUBMISSION_OUTREACH_2',
    SUBMISSION_OUTREACH_3: 'SUBMISSION_OUTREACH_3',
    SUBMISSION_OUTREACH_4: 'SUBMISSION_OUTREACH_4',
    SUBMISSION_OUTREACH_ADDTL: 'SUBMISSION_OUTREACH_ADDTL',
    SUBMITTED_VIA_CMM: 'SUBMITTED_VIA_CMM',
    SUBMITTED_VIA_EPA: 'SUBMITTED_VIA_EPA',
    APPROVED: 'APPROVED',
    DENIED: 'DENIED',
    DUPLICATE_CASE: 'DUPLICATE_CASE',
    CANCELED_BY_PRESCR: 'CANCELED_BY_PRESCR',
    DRUG_NOT_IN_SCOPE: 'DRUG_NOT_IN_SCOPE',
    NONFORMULARY: 'NONFORMULARY',
    COVERAGE_TERMINATED: 'COVERAGE_TERMINATED',
    REAUTHORIZATION: 'REAUTHORIZATION',
    MDO_UNRESPONSIVE: 'MDO_UNRESPONSIVE',
    QUESTIONS_AVAILABLE: 'QUESTIONS_AVAILABLE',
    QUESTIONS_SUBMITTED: 'QUESTIONS_SUBMITTED',
    PRIOR_AUTH_DENIED: 'PRIOR_AUTH_DENIED',
    NEW_PRIOR_AUTH_REQUEST_INITIATED: 'NEW_PRIOR_AUTH_REQUEST_INITIATED',
    PA_FAILED: 'PA_FAILED',
    REFERRAL_ASSIGNED: 'REFERRAL_ASSIGNED',
    REFERRAL_SERVICEABLE: 'REFERRAL_SERVICEABLE',
    PA_REQUEST_CANCELED: 'PA_REQUEST_CANCELED',
    PA_INITIATED: 'PA_INITIATED',
    SUBMITTED_TO_HEALTH_PLAN: 'SUBMITTED_TO_HEALTH_PLAN',
} as const;

export const StatusDetailsDisplayStrings = {
    [IntakeStatusDetails.PRESCR_NUMBER_MISSING]: 'Prescriber number missing',
    [IntakeStatusDetails.FAILED_FAX]: 'Failed fax',
    [IntakeStatusDetails.PRESCR_OUTREACH_1]: 'Prescriber outreach 1',
    [IntakeStatusDetails.PRESCR_OUTREACH_2]: 'Prescriber outreach 2',
    [IntakeStatusDetails.PRESCR_OUTREACH_3]: 'Prescriber outreach 3',
    [IntakeStatusDetails.PRESCR_OUTREACH_4]: 'Prescriber outreach 4',
    [IntakeStatusDetails.PRESCR_OUTREACH_ADDTL]: 'Additional prescriber outreach',
    [IntakeStatusDetails.PRESCRIBER_NOTIFICATION_OUTREACH]: 'Prior Auth Notification Fax',
    [IntakeStatusDetails.PRESCRIBER_NOTIFICATION_OUTREACH_2]: 'Prior Auth Notification Fax - Day 4',
    [IntakeStatusDetails.PRESCRIBER_NOTIFICATION_OUTREACH_3]:
        'Prior Auth Notification Fax - Day 10',
    [IntakeStatusDetails.FMH_TO_SUBMIT]: 'FMH to Submit',
    [IntakeStatusDetails.PRESCR_TO_SUBMIT]: 'Prescriber to Submit',
    [IntakeStatusDetails.PHARMACY_TO_SUBMIT]: 'Pharmacy to Submit',
    [IntakeStatusDetails.SUBMISSION_OUTREACH_1]: 'Submission Outreach 1',
    [IntakeStatusDetails.SUBMISSION_OUTREACH_2]: 'Submission Outreach 2',
    [IntakeStatusDetails.SUBMISSION_OUTREACH_3]: 'Submission Outreach 3',
    [IntakeStatusDetails.SUBMISSION_OUTREACH_4]: 'Submission Outreach 4',
    [IntakeStatusDetails.SUBMISSION_OUTREACH_ADDTL]: 'Additional submission outreach',
    [IntakeStatusDetails.SUBMITTED_VIA_CMM]: 'Submitted via Cover My Meds',
    [IntakeStatusDetails.SUBMITTED_VIA_EPA]: 'Submitted via ePA',
    [IntakeStatusDetails.APPROVED]: 'Approved',
    [IntakeStatusDetails.DENIED]: 'Denied',
    [IntakeStatusDetails.DUPLICATE_CASE]: 'Duplicate case',
    [IntakeStatusDetails.CANCELED_BY_PRESCR]: 'PA canceled by prescriber',
    [IntakeStatusDetails.DRUG_NOT_IN_SCOPE]: 'Drug not in scope',
    [IntakeStatusDetails.NONFORMULARY]: 'Nonformulary',
    [IntakeStatusDetails.COVERAGE_TERMINATED]: 'Coverage terminated',
    [IntakeStatusDetails.REAUTHORIZATION]: 'Reauthorization',
    [IntakeStatusDetails.MDO_UNRESPONSIVE]: 'MDO Unresponsive',
    [IntakeStatusDetails.QUESTIONS_AVAILABLE]: 'Questions available',
    [IntakeStatusDetails.QUESTIONS_SUBMITTED]: 'Questions submitted',
    [IntakeStatusDetails.PRIOR_AUTH_DENIED]: 'Prior auth denied',
    [IntakeStatusDetails.NEW_PRIOR_AUTH_REQUEST_INITIATED]: 'New prior auth request initiated',
    [IntakeStatusDetails.PA_FAILED]: 'PA Failed',
    [IntakeStatusDetails.REFERRAL_ASSIGNED]: 'Referral Assigned',
    [IntakeStatusDetails.REFERRAL_SERVICEABLE]: 'Referral Serviceable',
    [IntakeStatusDetails.PA_REQUEST_CANCELED]: 'PA request canceled',
    [IntakeStatusDetails.PA_INITIATED]: 'PA Initiated',
    [IntakeStatusDetails.SUBMITTED_TO_HEALTH_PLAN]: 'Submitted to health plan',
} as const;

export const LanguageOptions = ISO6391.getLanguages(ISO6391.getAllCodes()).sort((a, b) =>
    sortAsc(a.name, b.name),
);

/** Default place of service for Pharmacy benefit intake cases */
export const DEFAULT_PHARMACY_PLACE_OF_SERVICE = 'HOME';

export const AgoraTreatmentBaseUrl = (payerId: string) =>
    `${process.env.REACT_APP_BASE_URL.replace('api', 'agora')}/treatments/${payerId}/`;

/** Error Codes for Prior Auth Question Set */
export const PriorAuthErrorCodes = {
    BY: 'BY',
    BX: 'BX',
    CC: 'CC',
    CD: 'CD',
    CE: 'CE',
    CF: 'CF',
    CG: 'CG',
    CP: 'CP',
    CH: 'CH',
    CJ: 'CJ',
    CK: 'CK',
    CL: 'CL',
    CM: 'CM',
    CN: 'CN',
    CO: 'CO',
    CY: 'CY',
    FL: 'FL',
    FM: 'FM',
    FN: 'FN',
    FO: 'FO',
    FP: 'FP',
    FQ: 'FQ',
    FR: 'FR',
} as const;

export const PriorAuthErrorDisplayTextMap = {
    [PriorAuthErrorCodes.BY]: 'Other',
    [PriorAuthErrorCodes.BX]:
        'Electronic Prior Authorization not supported. Submit via other methods',
    [PriorAuthErrorCodes.CC]: 'Prior Authorization not required for patient/medication',
    [PriorAuthErrorCodes.CD]: 'Cannot find matching patient',
    [PriorAuthErrorCodes.CE]: 'Patient not eligible (does not have coverage with the payer)',
    [PriorAuthErrorCodes.CF]: 'Prior Authorization approved',
    [PriorAuthErrorCodes.CG]: 'Prior Authorization in process',
    [PriorAuthErrorCodes.CP]:
        'The receiver is not the PA processor for this patient and medication combination',
    [PriorAuthErrorCodes.CH]: 'Closed by health plan/payer/processor/PBM',
    [PriorAuthErrorCodes.CJ]: 'Closed by Provider',
    [PriorAuthErrorCodes.CK]: 'Closed by Member',
    [PriorAuthErrorCodes.CL]: 'Attachment type (mimetype) not supported',
    [PriorAuthErrorCodes.CM]: 'Prescriber not allowed to submit PA request',
    [PriorAuthErrorCodes.CN]: 'Response content is inconsistent with the question',
    [PriorAuthErrorCodes.CO]: 'The receiver is not the PA processor for this patient',
    [PriorAuthErrorCodes.CY]: 'Prior Authorization duplicate/denied',
    [PriorAuthErrorCodes.FL]: 'Provider responded after deadline to reply. PA must be reinitiated',
    [PriorAuthErrorCodes.FM]:
        'Product not covered by this plan. Prior Authorization not available.',
    [PriorAuthErrorCodes.FN]:
        'Prescription within prescribing limits. Prior Authorization not required for PATIENT/MEDICATION.',
    [PriorAuthErrorCodes.FO]:
        'Coverage limits may exist for quantity and/or days supply. Plan will pay up to coverage limit.',
    [PriorAuthErrorCodes.FP]: 'Coverage limits have been exceeded exception not available.',
    [PriorAuthErrorCodes.FQ]: 'Active PA on file.',
    [PriorAuthErrorCodes.FR]: 'Submitted Product Code is not valid. Please resolve and resubmit.',
} as const;

export const MilestoneType = {
    QUESTIONS_AVAILABLE: 'Questions Available',
    QUESTIONS_SUBMITTED: 'Questions Submitted',
    PA_DENIED: 'PA Denied',
    PRIOR_AUTH_DENIED: 'Prior Auth Denied',
    PA_APPROVED: 'PA Approved',
    PRIOR_AUTH_APPROVED: 'Prior Auth Approved',
    PA_FAILED: 'PA Failed',
    PA_CANCELLED: 'PA Cancelled',
} as const;

export const MilestoneTitleDisplayStrings = {
    [MilestoneType.PA_DENIED]: 'Prior Auth Denied',
    [MilestoneType.PRIOR_AUTH_DENIED]: 'Prior Auth Denied',
    [MilestoneType.PA_APPROVED]: 'Prior Auth Approved',
    [MilestoneType.PRIOR_AUTH_APPROVED]: 'Prior Auth Approved',
    [MilestoneType.QUESTIONS_AVAILABLE]: 'Questions Set Available Details',
    [MilestoneType.QUESTIONS_SUBMITTED]: 'Review Question Set',
    [MilestoneType.PA_FAILED]: 'Prior Auth Failed',
    [MilestoneType.PA_CANCELLED]: 'PA Cancelled',
} as const;

export const QuestionsAvailableMessage =
    'Please provide all information requested. Failure to complete this form in its entirety may result in delayed processing or an adverse determination for insufficient information.';

export const DeterminationToStatusMessageMap = {
    [Determination.DENIED]: { milestoneType: MilestoneType.PA_DENIED },
    [Determination.APPROVED]: { milestoneType: MilestoneType.PA_APPROVED },
    [Determination.OPEN]: { milestoneType: MilestoneType.QUESTIONS_AVAILABLE },
    [Determination.CLOSED]: { milestoneType: MilestoneType.PA_FAILED },
    [TransactionType.PA_CANCEL_RESPONSE]: { milestoneType: MilestoneType.PA_CANCELLED },
} as const;

export const QuestionSetStatus = {
    NO_PA_REQUEST_INITIATED: 'NO_PA_REQUEST',
    PA_CANCEL_REQUEST_INPROGRESS: 'PA_CANCEL_REQUEST_IN_PROGRESS',
    PA_CANCEL_REQUEST_COMPLETED: 'PA_CANCEL_REQUEST_COMPLETED',
} as const;

export const QuestionSetHeaderDisplayStrings = {
    [QuestionSetStatus.NO_PA_REQUEST_INITIATED]: 'No prior auth available',
    [QuestionSetStatus.PA_CANCEL_REQUEST_INPROGRESS]: 'No Prior auth request available.',
} as const;

export const QuestionSetBodyDisplayStrings = {
    [QuestionSetStatus.NO_PA_REQUEST_INITIATED]:
        'A prior auth request will be created when it has been determined that FMH will be responsible for submitting the prior auth.',
    [QuestionSetStatus.PA_CANCEL_REQUEST_INPROGRESS]:
        'The prior auth request has been canceled. A closed response from the PBM is in progress.',
} as const;

export const PriorAuthStatusMessages = {
    [TransactionType.PA_CANCEL_RESPONSE]: 'Prior auth has been canceled.',
} as const;

export const ErrorCodeStatusMessage = [PriorAuthErrorCodes.FQ, PriorAuthErrorCodes.CD];

export const MilestoneToStatusMessageMap = {
    [MilestoneType.PA_APPROVED]: {
        intent: InlineMessage.Intent.SUCCESS,
        label: MilestoneTitleDisplayStrings[MilestoneType.PA_APPROVED],
    },
    [MilestoneType.PRIOR_AUTH_APPROVED]: {
        intent: InlineMessage.Intent.SUCCESS,
        label: MilestoneTitleDisplayStrings[MilestoneType.PRIOR_AUTH_APPROVED],
    },
    [MilestoneType.PA_DENIED]: {
        intent: InlineMessage.Intent.ERROR,
        label: MilestoneTitleDisplayStrings[MilestoneType.PA_DENIED],
    },
    [MilestoneType.PA_FAILED]: {
        intent: InlineMessage.Intent.WARNING,
        label: MilestoneTitleDisplayStrings[MilestoneType.PA_FAILED],
    },
    [MilestoneType.PA_CANCELLED]: {
        intent: InlineMessage.Intent.INFO,
        label: PriorAuthStatusMessages[TransactionType.PA_CANCEL_RESPONSE],
    },
    [PriorAuthErrorCodes.FQ]: {
        intent: InlineMessage.Intent.WARNING,
        label: PriorAuthErrorDisplayTextMap[PriorAuthErrorCodes.FQ],
    },
} as const;

export const ActionMenuDialogs = {
    LOG_PRIOR_AUTH: 'log-prior-auth',
    ATTACH_DOCUMENTS: 'attach-documents',
    CANCEL_INTAKE_CASE: 'cancel-intake-case',
    REOPEN_INTAKE_CASE: 'reopen-intake-case',
    SEND_FAX: 'send-fax',
    CASE_ASSIGNMENT: 'case-assignment',
    PAUSE_INTAKE_CASE: 'pause-intake-case',
} as const;

export type ActionMenuDialogType = (typeof ActionMenuDialogs)[keyof typeof ActionMenuDialogs];

export const PendingActionTypes = {
    OPEN_OVERVIEW_TAB: 'open-overview-tab',
    OPEN_PRIOR_AUTH_TAB: 'open-prior-auth-tab',
    OPEN_ACTION_MENU_DIALOG: 'open-action-menu-dialog',
    OPEN_NOTES_TAB: 'open-notes-tab',
} as const;

export type PendingAction =
    | { type: typeof PendingActionTypes.OPEN_OVERVIEW_TAB }
    | { type: typeof PendingActionTypes.OPEN_PRIOR_AUTH_TAB }
    | {
          type: typeof PendingActionTypes.OPEN_ACTION_MENU_DIALOG;
          dialog: ActionMenuDialogType;
      }
    | {
          type: typeof PendingActionTypes.OPEN_NOTES_TAB;
      }
    | null;

export const ActionPlan = {
    FMH_TO_SUBMIT: 'FMH to submit',
    PRESCRIBER_TO_SUBMIT: 'Prescriber to submit',
    PHARMACY_TO_SUBMIT: 'Pharmacy to submit',
} as const;
